import React, { useEffect } from 'react';
import { Calculator, DollarSign, Eye, EyeOff, Unlock, Lock } from 'lucide-react';
import DateRangeModal from './date-range-modal';
import { LineItem, presetPacingRanges } from '../types/pacing.types';
import { useUpdateLineItemMutation } from '../endpoints/pacing.endpoints';
import { PacingViewApiResponse } from '../endpoints/pacing.types';
import { formatDateForDisplay, onPresetDateSelect } from '../utils/date-utils';
import { to2dp } from '../utils/pacing-utils';

interface LineRowProps {
  line: LineItem;
  view?: PacingViewApiResponse;
  isLastRow: boolean;
  showAll: boolean;
}

const LineRow: React.FC<LineRowProps> = ({ line, isLastRow, view, showAll }) => {
  const [budget, setBudget] = React.useState(Number(line.budget) ?? 0);
  const [dailyBudget, setDailyBudget] = React.useState(Number(line.daily_budget) ?? 0);
  const [dateLabel, setDateLabel] = React.useState(() => {
    const startDate = formatDateForDisplay(new Date(line.start_date + 'T00:00:00')); // Force local time
    const endDate = formatDateForDisplay(new Date(line.end_date + 'T00:00:00'));
    return `${startDate} - ${endDate}`;
  });
  const [isVisible, setIsVisible] = React.useState(line.is_visible == undefined ? true : line.is_visible);
  const [isLocked, setIsLocked] = React.useState(line.is_locked == undefined ? false : line.is_locked);

  const [updateLineItem, { isLoading: isUpdating }] = useUpdateLineItemMutation();

  useEffect(() => {
    setDateLabel(() => {
      const startDate = formatDateForDisplay(new Date(line.start_date + 'T00:00:00')); // Force local time
      const endDate = formatDateForDisplay(new Date(line.end_date + 'T00:00:00'));
      return `${startDate} - ${endDate}`;
    });
  }, [line.start_date, line.end_date]);

  useEffect(() => {
    setBudget(Number(line.budget) ?? 0);
    setDailyBudget(Number(line.daily_budget) ?? 0);
  }, [line.budget, line.daily_budget]);

  useEffect(() => {
    setIsVisible(line.is_visible == undefined ? true : line.is_visible);
    setIsLocked(line.is_locked == undefined ? false : line.is_locked);
  }, [line.is_visible, line.is_locked]);

  const handleUpdateLineItem = (field: string, value: any) => {
    if (view?.id) {
      updateLineItem({ lineItem: line, field, value, viewId: view.id });
    }
  };

  const handleUpdateLineItemDates = (startDate: string, endDate: string) => {
    if (view?.id) {
      updateLineItem({ lineItem: line, field: 'start_date', value: startDate, viewId: view.id });
      updateLineItem({ lineItem: line, field: 'end_date', value: endDate, viewId: view.id });
    }
  };

  const handleCalculateDailyBudget = () => {
    const spendRemaining = Number(line.budget ?? 0) - Number(line.spend ?? 0);
    const value = spendRemaining / Number(line.days_remaining ?? 0);
    handleUpdateLineItem('daily_budget', value > 0 ? value.toFixed(2) : 0);
  };

  if (!showAll && !isVisible) {
    return null;
  }

  return (
    <>
      <tr className={`max-w-1/4 hover:bg-gray-50 bg-gray-201 !border-l-yellow-500 !border-r-yellow-500 border-4 ${isLastRow ? '!border-b-yellow-500 !border-b-4 border-t-0' : 'border-b-1 border-t-0 border-gray-400'}`}>
        <td className="w-[3%] min-w-[15px] border-r border-gray-400 items-center justify-center" />
        <td className="!w-[25%] px-6 py-2 whitespace-nowrap border-r border-gray-400">
          <div className='w-full flex flex-col items-left'>

            <div className='flex items-center justify-between'>
              <div className="text-sm font-medium text-gray-900">
                {line.line_item_name}
                {/* <span className='text-xs text-gray-500 ml-2'>
                  {line.account.source?.replace('_', ' ').toLowerCase()}
                </span> */}
              </div>
              <div>
                <div>
                  {showAll && (
                    <div className='flex items-center gap-2'>
                      <div
                        onClick={() => {setIsVisible(!isVisible); handleUpdateLineItem('is_visible', !isVisible);}}
                        className='cursor-pointer ml-2'
                      >
                        {isVisible ? <Eye className="h-4 w-4 bg-blue-500 hover:bg-blue-400 text-white rounded-sm p-0.5 cursor-pointer" /> : <EyeOff className="h-4 w-4 bg-gray-201 text-black hover:bg-gray-200 rounded-sm p-0.5 cursor-pointer" />}
                      </div>
                      <div
                        onClick={() => {setIsLocked(!isLocked); handleUpdateLineItem('is_locked', !isLocked);}}
                        className='cursor-pointer'
                      >
                        {isLocked ? <Lock className="h-4 w-4 bg-orange-501 text-white rounded-sm hover:bg-orange-300 p-0.5 cursor-pointer" /> : <Unlock className="h-4 w-4 bg-gray-201 text-black hover:bg-gray-200 rounded-sm p-0.5 cursor-pointer" />}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='flex items-center text-xs text-gray-500'>
              <div
                className={`w-2 h-2 rounded-full mr-1 ${line?.pacing_item_status === 'on_track' ? 'bg-green-501' : line?.pacing_item_status === 'ahead' ? 'bg-red-501' : line?.pacing_item_status === 'behind' ? 'bg-yellow-500' : line?.pacing_item_status === 'update' ? 'bg-orange-501' : 'bg-gray-500' }`}
              />
              {line?.pacing_item_status?.toLocaleLowerCase().replace('_', ' ')}
              <div className='w-full ml-4 bg-gray-200 rounded-full h-2 text-center'>
                <div
                  className='bg-blue-500 h-2 rounded-full text-gray-200 items-center justify-center'
                  style={{ width: `${Math.min(Number(line?.percent_complete ?? 0), 100).toFixed(0)}%` }}
                >
                  {/* {line?.percentComplete.toFixed(0)}
                  % */}
                </div>
              </div>
            </div>

          </div>
        </td>
        <td className="w-[100px] px-2 py-2 whitespace-nowrap border-r border-gray-400">
          <div
            className="flex justify-center items-center text-sm text-gray-900 font-bold"
          >
            <DollarSign className="w-4 h-4 mr-1" />
            <input
              type="text"
              inputMode="decimal"
              pattern="^\d+(\.\d{1,2})?$"
              value={budget}
              onChange={(e) => {setBudget(Number(to2dp(e.target.value)));}}
              onBlur={(e) => {handleUpdateLineItem('budget', e.target.value);}}
              className="w-[50px] mr-2 font-bold text-sm text-gray-900 bg-gray-201 focus:outline-none border-b-1 border-gray-400"
            />
          </div>
        </td>
        <td className="px-4 py-2 whitespace-nowrap border-r border-gray-400">
          <div
            className="flex justify-center items-center text-sm text-gray-900 font-bold"
          >
            <DollarSign className="w-4 h-4 mr-1" />
            <input
              type="text"
              inputMode="decimal"
              pattern="^\d+(\.\d{1,2})?$"
              value={dailyBudget}
              onChange={(e) => {setDailyBudget(Number(to2dp(e.target.value)));}}
              onBlur={(e) => {handleUpdateLineItem('daily_budget', e.target.value);}}
              className="w-[60px] mr-2 font-bold text-sm text-gray-900 bg-gray-201 focus:outline-none border-b-1 border-gray-400"
            />

            <button
              onClick={() => handleCalculateDailyBudget()}
              className='text-black rounded-full p-1'
            >
              <Calculator className="w-4 h-4" />
            </button>
          </div>
        </td>
        <td className="w-[100px] px-6 py-2 whitespace-nowrap border-r border-gray-400">
          <div className="text-sm text-gray-900 font-bold">
            $
            {Number(line.spend ?? 0).toFixed(2) ?? '0'}
          </div>
        </td>
        <td className="px-2 py-1 whitespace-nowrap">
          <div className="flex text-sm text-gray-900">
            <DateRangeModal
              disclaimer='Note: Start Dates can only go back a maximum of 45 days'
              selectedDate={new Date(line.start_date ?? '').toLocaleDateString()}
              onDateChange={(filter, startDate, endDate) => handleUpdateLineItemDates(startDate, endDate)}
              className={'text-sm font-bold'}
              selectedClassName={''}
              label={dateLabel}
              selectedStartDate={line.start_date}
              selectedEndDate={line.end_date}
              presetRanges={presetPacingRanges}
              onPresetSelect={(preset) => onPresetDateSelect(preset)}
            />
          </div>
        </td>
      </tr>
    </>
  );
};

export default LineRow;
